import { Button, Text } from "@merge-api/merge-javascript-shared";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { HTTPMethod } from "../../../../models/HTTPMethods";
import { navigateToIntegrationBuilderCreateAPIEndpoint } from "../../../../router/RouterUtils";
import MergeTable from "../../../shared/MergeTable";
import TableSkeletonLoader from "../../../shared/TableSkeletonLoader";
import useIntegrationBuilderContext from "../../context/useIntegrationBuilderContext";
import IntegrationBuilderEmptyState from "../../shared/IntegrationBuilderEmptyState";
import IntegrationBuilderHeader from "../../shared/IntegrationBuilderHeader";
import { APIEndpointIntegrationBuilder } from "../../utils/Entities";
import APIEndpointExplain from "./APIEndpointExplain";
import APIEndpointTableRow from "./APIEndpointTableRow";
import HTTPMethodColumnHeader from "./HTTPMethodColumnHeader";
import usePollAPIEndpoints from "../hooks/usePollAPIEndpoints";
import { Plus } from "lucide-react";

interface APIEndpointsTableProps {
  integrationID: string;
  apiEndpoints: APIEndpointIntegrationBuilder[] | undefined;
  setAPIEndpoints: React.Dispatch<
    React.SetStateAction<APIEndpointIntegrationBuilder[] | undefined>
  >;
  isLoadingAPIEndpoints: boolean;
  setIsLoadingAPIEndpoints: React.Dispatch<React.SetStateAction<boolean>>;
}

const API_ENDPOINT_SUBMIT_TEXT = "Save API endpoint";

const APIEndpointsTable = ({
  integrationID,
  apiEndpoints,
  setAPIEndpoints,
  isLoadingAPIEndpoints,
  setIsLoadingAPIEndpoints,
}: APIEndpointsTableProps) => {
  //hooks
  const history = useHistory();
  // context
  useIntegrationBuilderContext({
    submitButtonText: API_ENDPOINT_SUBMIT_TEXT,
    canSubmit: false,
    shouldRenderSubmitButton: false,
    shouldRenderNavigationButtons: true,
    shouldRenderDeleteButton: false,
    markForDeletion: false,
    shouldRenderStageButton: false,
  });

  // state
  const [filteredAPIEndpoints, setFilteredAPIEndpoints] = useState<
    APIEndpointIntegrationBuilder[] | undefined
  >(apiEndpoints);
  const [methodFilter, setMethodFilter] = useState<HTTPMethod | null>(null);

  // Set up the main table of API endpoints
  useEffect(() => {
    if (apiEndpoints) {
      if (methodFilter) {
        setFilteredAPIEndpoints(
          apiEndpoints.filter(
            (apiEndpoint) => apiEndpoint.request_configuration.method === methodFilter
          )
        );
      } else {
        setFilteredAPIEndpoints(apiEndpoints);
      }
    }
  }, [methodFilter, apiEndpoints]);

  const { pollAPIEndpoints, stopPollingAPIEndpoints } = usePollAPIEndpoints({
    setAPIEndpoints,
    setIsLoadingAPIEndpoints,
  });

  // sets up and polls the API endpoints on mount
  // stops the polling on unmount
  useEffect(() => {
    pollAPIEndpoints(integrationID);
    return () => {
      stopPollingAPIEndpoints();
    };
  }, [pollAPIEndpoints, integrationID]);

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-grow">
        <IntegrationBuilderHeader
          title="API endpoints"
          button={
            <Button
              size="md"
              leftIcon={<Plus size={16} />}
              onClick={() => {
                navigateToIntegrationBuilderCreateAPIEndpoint(history, integrationID);
              }}
            >
              Endpoint
            </Button>
          }
        />
        <APIEndpointExplain className="mb-6" />
        <MergeTable
          hover={!isLoadingAPIEndpoints && filteredAPIEndpoints && filteredAPIEndpoints.length > 0}
          hasMarginBottom={false}
          hasMorePaddingOnFirstElement
          className="mb-8" // gives space for the sticky button
          header={
            <>
              <th scope="col">Name</th>
              <th scope="col">
                <HTTPMethodColumnHeader
                  methodFilter={methodFilter}
                  setMethodFilter={setMethodFilter}
                />
              </th>
              <th scope="col">Path</th>
              <th></th>
            </>
          }
          content={
            isLoadingAPIEndpoints ? (
              <TableSkeletonLoader cols={4} rows={3} />
            ) : (
              <>
                {filteredAPIEndpoints && filteredAPIEndpoints.length > 0 ? (
                  filteredAPIEndpoints.map((apiEndpoint, index) => (
                    <APIEndpointTableRow
                      key={index}
                      apiEndpoint={apiEndpoint}
                      integrationID={integrationID}
                      hasButtonHeader={true}
                      setAPIEndpoints={setAPIEndpoints}
                      canDuplicate={true}
                    />
                  ))
                ) : (
                  <IntegrationBuilderEmptyState
                    title="No API endpoints"
                    subtitle={
                      <div>
                        Click <Text variant="title-sm">+ Endpoint</Text> in the top right to
                        manually add one or <Text variant="title-sm">autogenerate </Text>
                        endpoints below with AI
                      </div>
                    }
                    numberOfColumns={3}
                  />
                )}
              </>
            )
          }
        />
      </div>
    </div>
  );
};

export default APIEndpointsTable;
